import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import {
  DEFAULT_RULE,
  type AllowRow,
  type AllowState,
  type CapabilityException,
  type ExceptionScope,
  type FleetColumn,
} from '@/lib/reference'
import {
  exceptionIndex,
  fleetColumns,
  resolveAirport,
  resolveCityPair,
  resolveCountry,
} from '@/lib/reference/fleet'
import { airportGroupIndex, countryGroupIndex } from '@/lib/reference/groups'
import { CapabilityMatrix, type MatrixRow } from '@/components/reference/CapabilityMatrix'
import { ExceptionDialog } from '@/components/reference/ExceptionDialog'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'

type Tab = 'route' | 'country' | 'airport' | 'exception'

/** Which table a staged cell belongs to. */
type MatrixTable = 'cityPairCapability' | 'countryCapability' | 'airportCapability'

interface Staged {
  table: MatrixTable
  id: string
  allow: AllowRow
}

const SCOPE_LABEL: Record<ExceptionScope, string> = {
  cityPair: 'City pair',
  country: 'Country',
  airport: 'Airport',
}

/**
 * The three matrices that decide which aircraft may work where.
 *
 * This is the aircraft tier, and it is the baseline: the standing rule, the
 * lowest layer, keyed by the group/category/type trio. It cannot answer for
 * an individual tail — 22 of the 32 NEO non ACT tails are barred from Russia
 * while the trio as a whole is permitted there. The four limits on each
 * aircraft row are the second tier, and both have to pass.
 *
 * Departures from the baseline are made on the City pairs, Countries and
 * Airports screens and are gathered back here under Exceptions, so there is
 * one place that answers "what are we currently doing that the rule does not
 * say".
 *
 * Cells stage into a draft and commit on Save. Clicking a cell is an edit to
 * reference data, and reference data that changes under the pointer is how a
 * constraint check goes wrong without anybody noticing.
 */
export function CapabilityPage() {
  const { can } = useAuth()
  const {
    aircraft,
    airports,
    countries,
    cityPairs,
    cityPairCapability,
    countryCapability,
    airportCapability,
    capabilityException,
    airportGroups,
    countryGroups,
    manifest,
    create,
    update,
    remove,
  } = useReference()
  const [tab, setTab] = useState<Tab>('route')
  const [adding, setAdding] = useState<'country' | 'airport' | null>(null)
  const [editing, setEditing] = useState<CapabilityException | null>(null)
  const [draft, setDraft] = useState<Record<string, Staged>>({})
  const [saving, setSaving] = useState(false)

  const editable = can('settings.edit')

  /** Columns come from the fleet, so a renamed group moves its column with it. */
  const columns = useMemo(() => {
    const derived = fleetColumns(aircraft)
    return derived.length ? derived : (manifest?.fleetColumns ?? [])
  }, [aircraft, manifest])

  const countryNames = useMemo(
    () => new Map(countries.map((row) => [row.code, row.name] as const)),
    [countries],
  )
  const airportNames = useMemo(
    () => new Map(airports.map((row) => [row.iata, row.name] as const)),
    [airports],
  )
  const pairKeys = useMemo(() => new Set(cityPairs.map((row) => row.key)), [cityPairs])

  const airportGroupMembers = useMemo(
    () => airportGroupIndex(airportGroups, airports, airportCapability),
    [airportGroups, airports, airportCapability],
  )
  const countryGroupMembers = useMemo(
    () => countryGroupIndex(countryGroups, countries, airports),
    [countryGroups, countries, airports],
  )
  const groupNames = useMemo(
    () =>
      new Map([...airportGroups, ...countryGroups].map((group) => [group.code, group.name])),
    [airportGroups, countryGroups],
  )

  const pairExceptions = useMemo(
    () => exceptionIndex(capabilityException, 'cityPair'),
    [capabilityException],
  )
  const countryExceptions = useMemo(
    () => exceptionIndex(capabilityException, 'country'),
    [capabilityException],
  )
  const airportExceptions = useMemo(
    () => exceptionIndex(capabilityException, 'airport'),
    [capabilityException],
  )

  const routeRows = useMemo<MatrixRow[]>(
    () =>
      cityPairCapability.map((rule) => ({
        id: rule.id,
        label: rule.cityPairKey,
        sublabel:
          rule.cityPairKey === DEFAULT_RULE
            ? 'every city pair without a row of its own'
            : pairKeys.has(rule.cityPairKey)
              ? undefined
              : 'not in the city pair table',
        allow: draft[`cityPairCapability:${rule.id}`]?.allow ?? rule.allow,
        exception: pairExceptions.get(rule.cityPairKey)?.allow,
      })),
    [cityPairCapability, pairKeys, pairExceptions, draft],
  )

  /** A group row says what it covers; a place row says what it is. */
  const subjectLabel = useCallback(
    (
      subject: string,
      kind: 'station' | 'country' | 'group',
      members: Map<string, Set<string>>,
      names: Map<string, string>,
      missing: string,
    ) => {
      if (kind !== 'group') return names.get(subject) ?? missing
      const size = members.get(subject)?.size
      if (size === undefined) return 'group not in the group table'
      return `${groupNames.get(subject) ?? ''} · ${size} members`
    },
    [groupNames],
  )

  const countryRows = useMemo<MatrixRow[]>(
    () =>
      countryCapability.map((rule) => ({
        id: rule.id,
        label: rule.subject,
        kind: rule.subjectKind,
        sublabel: subjectLabel(
          rule.subject,
          rule.subjectKind,
          countryGroupMembers,
          countryNames,
          'not in the country table',
        ),
        allow: draft[`countryCapability:${rule.id}`]?.allow ?? rule.allow,
        exception:
          rule.subjectKind === 'country' ? countryExceptions.get(rule.subject)?.allow : undefined,
        // The tail counts are what the verdict was read off, so they stay
        // reachable: "yes" on a trio of 32 can still mean 10 tails.
        cellDetail: (column) => {
          const tails = rule.tails?.[column]
          return tails ? `${tails[0]} of ${tails[1]} tails` : undefined
        },
      })),
    [countryCapability, countryNames, countryExceptions, countryGroupMembers, subjectLabel, draft],
  )

  const airportRows = useMemo<MatrixRow[]>(
    () =>
      airportCapability.map((rule) => ({
        id: rule.id,
        label: rule.subject,
        kind: rule.subjectKind,
        sublabel: subjectLabel(
          rule.subject,
          rule.subjectKind,
          airportGroupMembers,
          airportNames,
          'not in the airport table',
        ),
        allow: draft[`airportCapability:${rule.id}`]?.allow ?? rule.allow,
        exception:
          rule.subjectKind === 'station' ? airportExceptions.get(rule.subject)?.allow : undefined,
      })),
    [airportCapability, airportNames, airportExceptions, airportGroupMembers, subjectLabel, draft],
  )

  /** Subjects that have no rule yet — groups first, they cover more ground. */
  const countryOptions = useMemo<SubjectOption[]>(
    () => [
      ...countryGroups
        .filter(
          (group) =>
            !countryCapability.some(
              (rule) => rule.subjectKind === 'group' && rule.subject === group.code,
            ),
        )
        .map((group) => ({ value: group.code, label: group.name, kind: 'group' as const })),
      ...countries
        .filter(
          (row) =>
            !countryCapability.some(
              (rule) => rule.subjectKind === 'country' && rule.subject === row.code,
            ),
        )
        .map((row) => ({ value: row.code, label: row.name, kind: 'country' as const })),
    ],
    [countries, countryGroups, countryCapability],
  )

  const airportOptions = useMemo<SubjectOption[]>(
    () => [
      ...airportGroups
        .filter(
          (group) =>
            !airportCapability.some(
              (rule) => rule.subjectKind === 'group' && rule.subject === group.code,
            ),
        )
        .map((group) => ({ value: group.code, label: group.name, kind: 'group' as const })),
      ...airports
        .filter(
          (row) =>
            row.iata &&
            !airportCapability.some(
              (rule) => rule.subjectKind === 'station' && rule.subject === row.iata,
            ),
        )
        .map((row) => ({ value: row.iata, label: row.name, kind: 'station' as const })),
    ],
    [airports, airportGroups, airportCapability],
  )

  /** Baseline with no exception applied — what an exception is measured against. */
  const baselineFor = useMemo(
    () =>
      (scope: ExceptionScope, subject: string) =>
        (column: string): AllowState => {
          if (scope === 'cityPair') {
            return resolveCityPair(cityPairCapability, undefined, subject, column).state
          }
          if (scope === 'country') {
            return resolveCountry(countryCapability, undefined, subject, column, countryGroupMembers)
              .state
          }
          return resolveAirport(airportCapability, undefined, subject, column, airportGroupMembers)
            .state
        },
    [
      cityPairCapability,
      countryCapability,
      airportCapability,
      countryGroupMembers,
      airportGroupMembers,
    ],
  )

  const table: MatrixTable =
    tab === 'country'
      ? 'countryCapability'
      : tab === 'airport'
        ? 'airportCapability'
        : 'cityPairCapability'

  const rows = tab === 'country' ? countryRows : tab === 'airport' ? airportRows : routeRows
  const staged = Object.values(draft)
  const changedIds = useMemo(
    () => new Set(staged.filter((item) => item.table === table).map((item) => item.id)),
    [staged, table],
  )

  const unsetCells = useMemo(
    () =>
      [...routeRows, ...countryRows, ...airportRows].reduce(
        (total, row) =>
          total + columns.filter((column) => (row.allow[column.key] ?? 'unset') === 'unset').length,
        0,
      ),
    [routeRows, countryRows, airportRows, columns],
  )

  async function save() {
    setSaving(true)
    try {
      // One row at a time: the provider is the only thing that knows how a
      // write is applied, and a partial failure must leave the rest alone.
      for (const item of staged) await update(item.table, item.id, { allow: item.allow })
      setDraft({})
    } finally {
      setSaving(false)
    }
  }

  /** An empty `allow` means the exception no longer differs, so it goes. */
  async function saveException(
    scope: ExceptionScope,
    subject: string,
    allow: AllowRow,
    reason: string,
  ) {
    const existing = capabilityException.find(
      (row) => row.scope === scope && row.subject === subject,
    )
    if (Object.keys(allow).length === 0) {
      if (existing) await remove('capabilityException', existing.id)
      return
    }
    if (existing) await update('capabilityException', existing.id, { allow, reason })
    else await create('capabilityException', { scope, subject, allow, reason })
  }

  return (
    <section>
      <h1 className="t-title-lg text-ink">Capability</h1>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex rounded-[100px] bg-surface-strong p-1">
          <TabButton active={tab === 'route'} onClick={() => setTab('route')}>
            City pairs {routeRows.length > 0 && <span className="t-number-sm">{routeRows.length}</span>}
          </TabButton>
          <TabButton active={tab === 'country'} onClick={() => setTab('country')}>
            Countries {countryRows.length > 0 && <span className="t-number-sm">{countryRows.length}</span>}
          </TabButton>
          <TabButton active={tab === 'airport'} onClick={() => setTab('airport')}>
            Airports {airportRows.length > 0 && <span className="t-number-sm">{airportRows.length}</span>}
          </TabButton>
          <TabButton active={tab === 'exception'} onClick={() => setTab('exception')}>
            Exceptions{' '}
            {capabilityException.length > 0 && (
              <span className="t-number-sm">{capabilityException.length}</span>
            )}
          </TabButton>
        </div>

        {tab !== 'exception' && unsetCells > 0 && (
          <p className="t-caption text-muted">
            <span className="t-number-sm text-ink">{unsetCells}</span> not set
          </p>
        )}

        <div className="ml-auto flex items-center gap-3">
          {staged.length > 0 && (
            <>
              <p className="t-caption text-muted">
                <span className="t-number-sm text-ink">{staged.length}</span> row
                {staged.length === 1 ? '' : 's'} edited
              </p>
              <Button variant="secondary" onClick={() => setDraft({})} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save()} loading={saving}>
                Save
              </Button>
            </>
          )}

          {editable && staged.length === 0 && (tab === 'country' || tab === 'airport') && (
            <Button onClick={() => setAdding(tab)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              Add
            </Button>
          )}
        </div>
      </div>

      {tab === 'exception' ? (
        <div className="mt-4">
          <ExceptionTable
            rows={capabilityException}
            columns={columns}
            names={{ country: countryNames, airport: airportNames }}
            onSelect={setEditing}
          />
        </div>
      ) : (
        <>
          {/* Rows keep seed order, which follows the city pair table rather
              than the alphabet, so the narrow routes stay together at the top. */}
          <div className="mt-4">
            <CapabilityMatrix
              rowHeader={tab === 'country' ? 'Country' : tab === 'airport' ? 'Airport' : 'City pair'}
              columns={columns}
              rows={rows}
              canEdit={editable}
              changedIds={changedIds}
              onChange={(id, allow) =>
                setDraft((current) => ({ ...current, [`${table}:${id}`]: { table, id, allow } }))
              }
            />
          </div>

          {(tab === 'country' || tab === 'airport') && (
            <p className="t-caption mt-4 text-muted">
              <span className="t-number-sm text-ink">
                {(tab === 'country' ? countryOptions : airportOptions).length}
              </span>{' '}
              {tab === 'country' ? 'countries' : 'airports'} and groups without a rule
            </p>
          )}
        </>
      )}

      {adding && (
        <AddRuleDialog
          title={adding === 'country' ? 'Add country rule' : 'Add airport rule'}
          label={adding === 'country' ? 'Country or group' : 'Airport or group'}
          options={adding === 'country' ? countryOptions : airportOptions}
          onCancel={() => setAdding(null)}
          onSubmit={async (option) => {
            const allow = Object.fromEntries(
              columns.map((column) => [column.key, 'unset']),
            ) as AllowRow
            if (adding === 'country') {
              await create('countryCapability', {
                subject: option.value,
                subjectKind: option.kind === 'group' ? 'group' : 'country',
                allow,
                tails: {},
              })
            } else {
              await create('airportCapability', {
                subject: option.value,
                subjectKind: option.kind === 'group' ? 'group' : 'station',
                allow,
              })
            }
            setAdding(null)
          }}
        />
      )}

      {editing && (
        <ExceptionDialog
          subject={editing.subject}
          subtitle={
            editing.scope === 'country'
              ? countryNames.get(editing.subject)
              : editing.scope === 'airport'
                ? airportNames.get(editing.subject)
                : SCOPE_LABEL[editing.scope]
          }
          columns={columns}
          baseline={baselineFor(editing.scope, editing.subject)}
          current={editing}
          canEdit={editable}
          onClose={() => setEditing(null)}
          onSave={(allow, reason) =>
            saveException(editing.scope, editing.subject, allow, reason)
          }
        />
      )}
    </section>
  )
}

const GLYPH: Record<AllowState, string> = { yes: '✓', no: '✕', unset: '—' }
const TONE: Record<AllowState, string> = {
  yes: 'text-semantic-up',
  no: 'text-semantic-down',
  unset: 'text-muted-soft',
}

/**
 * Every standing exception, in one list.
 *
 * The three matrices say what the rule is; this says where the operation
 * currently departs from it, who decided that and when. A deviation nobody
 * can list is a deviation nobody takes back off.
 */
function ExceptionTable({
  rows,
  columns,
  names,
  onSelect,
}: {
  rows: CapabilityException[]
  columns: FleetColumn[]
  names: { country: Map<string, string>; airport: Map<string, string> }
  onSelect: (row: CapabilityException) => void
}) {
  const label = (row: CapabilityException) =>
    row.scope === 'country'
      ? names.country.get(row.subject)
      : row.scope === 'airport'
        ? names.airport.get(row.subject)
        : undefined

  return (
    <div className="overflow-x-auto rounded-[16px] border border-hairline">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-hairline bg-surface-soft">
            <th scope="col" className="t-caption-strong px-4 py-3 text-ink">
              Scope
            </th>
            <th scope="col" className="t-caption-strong px-4 py-3 text-ink">
              Subject
            </th>
            <th scope="col" className="t-caption-strong px-4 py-3 text-ink">
              Override
            </th>
            <th scope="col" className="t-caption-strong px-4 py-3 text-ink">
              Reason
            </th>
            <th scope="col" className="t-caption-strong px-4 py-3 text-ink">
              By
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="t-body-sm px-4 py-10 text-center text-muted">
                No rows
              </td>
            </tr>
          )}

          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row)}
              className="cursor-pointer border-b border-hairline-soft last:border-0 hover:bg-surface-soft"
            >
              <td className="t-body-sm px-4 py-3 text-body">{SCOPE_LABEL[row.scope]}</td>
              <td className="px-4 py-3">
                <span className="t-number-sm block text-ink">{row.subject}</span>
                {label(row) && <span className="t-caption block text-muted">{label(row)}</span>}
              </td>
              <td className="px-4 py-3">
                <ul>
                  {columns
                    .filter((column) => row.allow[column.key])
                    .map((column) => (
                      <li key={column.key} className="t-body-sm whitespace-nowrap text-body">
                        <span className={`t-title-sm ${TONE[row.allow[column.key]]}`}>
                          {GLYPH[row.allow[column.key]]}
                        </span>{' '}
                        {column.group} · {column.category} · {column.type}
                      </li>
                    ))}
                </ul>
              </td>
              <td className="t-body-sm px-4 py-3 text-body">
                {row.reason || <span className="text-muted-soft">—</span>}
              </td>
              <td className="px-4 py-3">
                <span className="t-body-sm block text-body">{row.updatedBy ?? '—'}</span>
                {row.updatedAt && (
                  <span className="t-number-sm block text-muted">
                    {row.updatedAt.slice(0, 16).replace('T', ' ')}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A rule can be written against one place or against a group of them. */
interface SubjectOption {
  value: string
  label: string
  kind: 'station' | 'country' | 'group'
}

function AddRuleDialog({
  title,
  label,
  options,
  onCancel,
  onSubmit,
}: {
  title: string
  label: string
  options: SubjectOption[]
  onCancel: () => void
  onSubmit: (option: SubjectOption) => Promise<void>
}) {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)

  // Capped and typed-into rather than listing every airport: 8.672 options in
  // the DOM cost more than they help. Groups come first — there are few of
  // them and they are what a new rule usually wants.
  const matches = useMemo(() => {
    const needle = value.trim().toUpperCase()
    return options
      .filter((option) => !needle || option.value.startsWith(needle))
      .slice(0, 20)
  }, [options, value])

  // Group first, so a code that is both (DOM is also Dominica) makes the rule
  // the wider one — which is what somebody typing DOM means.
  const picked = options.find(
    (option) => option.value === value.trim().toUpperCase() && option.kind === 'group',
  ) ?? options.find((option) => option.value === value.trim().toUpperCase())

  return (
    <Modal title={title} onClose={onCancel} width="420px">
      <div className="mt-6">
        <label htmlFor="capability-subject" className="t-title-sm mb-2 block text-ink">
          {label}
        </label>
        <input
          id="capability-subject"
          list="capability-subject-options"
          value={value}
          onChange={(event) => setValue(event.target.value.toUpperCase())}
          className="t-body-md h-12 w-full rounded-[12px] border border-hairline bg-canvas px-4
                     text-ink outline-none focus:border-2 focus:border-primary focus:px-[15px]"
        />
        <datalist id="capability-subject-options">
          {matches.map((option) => (
            <option key={`${option.kind}:${option.value}`} value={option.value}>
              {option.kind === 'group' ? `${option.label} · group` : option.label}
            </option>
          ))}
        </datalist>
        {picked && (
          <p className="t-caption mt-2 text-muted">
            {picked.label}
            {picked.kind === 'group' && ' · group'}
          </p>
        )}
      </div>

      <div className="mt-8 flex gap-3">
        <Button
          fullWidth
          loading={saving}
          disabled={!picked}
          onClick={() => {
            if (!picked) return
            setSaving(true)
            onSubmit(picked).catch(() => setSaving(false))
          }}
        >
          Save
        </Button>
        <Button variant="secondary" onClick={onCancel} fullWidth>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`t-caption-strong inline-flex items-center gap-1.5 rounded-[100px] px-3.5 py-2
                  transition-colors ${active ? 'bg-canvas text-ink' : 'text-muted hover:text-body'}`}
    >
      {children}
    </button>
  )
}
